import type { Project, ProjectUnitArea, ProjectStatus, UnitAreaStatus } from "../../../domain/project/project-domain.js";

/**
 * Extraída para permitir um repositório SQLite real
 * (`SqliteProjectRepository`, correção multiusuário) no lugar desta classe
 * em memória, sem alterar quem a consome (`ProjectService`,
 * `ServiceFrontService`).
 */
export interface ProjectRepository {
  listProjects(filter?: { clientId?: string; status?: ProjectStatus }): Promise<Project[]>;
  getProject(id: string): Promise<Project | null>;
  saveProject(project: Project): Promise<Project>;
  deleteProject(id: string): Promise<void>;
  listUnitAreas(filter?: { projectId?: string; clientId?: string; status?: UnitAreaStatus }): Promise<ProjectUnitArea[]>;
  listPendingUnitAreas(clientId?: string): Promise<ProjectUnitArea[]>;
  bindUnitAreaToProject(unitAreaId: string, projectId: string, clientId: string): Promise<ProjectUnitArea | null>;
  getUnitArea(id: string): Promise<ProjectUnitArea | null>;
  saveUnitArea(unitArea: ProjectUnitArea): Promise<ProjectUnitArea>;
  deleteUnitArea(id: string): Promise<void>;
}

export class InMemoryProjectRepository implements ProjectRepository {
  private projects: Map<string, Project> = new Map();
  private unitAreas: Map<string, ProjectUnitArea> = new Map();
  private initialized = false;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const storedProjects = localStorage.getItem('inspect_projects');
      if (storedProjects) {
        const parsed: Project[] = JSON.parse(storedProjects);
        parsed.forEach(p => this.projects.set(p.id, p));
      }

      const storedUnits = localStorage.getItem('inspect_project_unit_areas');
      if (storedUnits) {
        const parsed: ProjectUnitArea[] = JSON.parse(storedUnits);
        parsed.forEach(u => this.unitAreas.set(u.id, u));
      }

      // Legacy Migration check
      this.migrateLegacyAreasIfNeeded();
    } catch (e) {
      console.warn("Failed to load projects from storage:", e);
    }
  }

  private saveToStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem('inspect_projects', JSON.stringify(Array.from(this.projects.values())));
      localStorage.setItem('inspect_project_unit_areas', JSON.stringify(Array.from(this.unitAreas.values())));
    } catch (e) {
      console.warn("Failed to save projects to storage:", e);
    }
  }

  /**
   * Rule #6: MIGRAÇÃO DE DADOS EXISTENTES
   * Se existirem ÁREAS já cadastradas no sistema atual:
   * - Converter a estrutura para que as antigas Áreas se tornem Unidades/Áreas
   * - Se não houver projeto, associar a um projeto inicial por cliente ("PROJETO OPERACIONAL BASE")
   */
  private migrateLegacyAreasIfNeeded(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const legacyAreasRaw = localStorage.getItem('inspect_areas');
      if (!legacyAreasRaw) return;

      const legacyAreas: Array<{ id: string; clientId: string; name: string; description?: string; active?: boolean }> = JSON.parse(legacyAreasRaw);
      if (!Array.isArray(legacyAreas) || legacyAreas.length === 0) return;

      let changed = false;

      // Group legacy areas by client
      const areasByClient = new Map<string, typeof legacyAreas>();
      for (const a of legacyAreas) {
        const cId = a.clientId;
        if (!cId || cId === 'CLIENT-DEFAULT' || cId === 'mock-client') continue;
        if (!areasByClient.has(cId)) areasByClient.set(cId, []);
        areasByClient.get(cId)!.push(a);
      }

      for (const [clientId, areas] of areasByClient.entries()) {
        // Find existing project for this client or create base project
        let project = Array.from(this.projects.values()).find(p => p.clientId === clientId);
        if (!project) {
          const baseProjectId = `PRJ-BASE-${clientId.substring(0, 8)}`;
          project = {
            id: baseProjectId,
            code: 'PRJ-BASE',
            name: 'Projeto Operacional Base',
            clientId: clientId,
            description: 'Projeto operacional base gerado automaticamente para migração de áreas históricas.',
            status: 'ACTIVE',
            startDate: (new Date().toISOString().split('T')[0] || '2026-01-01'),
            expectedEndDate: null,
            actualEndDate: null,
            manager: 'Coordenação de Integridade',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          this.projects.set(project.id, project);
          changed = true;
        }

        const currentProjectId = project.id;
        // Now migrate areas to unitAreas if not already present
        for (const la of areas) {
          if (!this.unitAreas.has(la.id)) {
            const unitArea: ProjectUnitArea = {
              id: la.id,
              code: la.name.substring(0, 6).toUpperCase().replace(/\s+/g, '-'),
              name: la.name,
              projectId: currentProjectId,
              clientId: clientId,
              description: la.description || null,
              status: la.active === false ? 'INACTIVE' : 'ACTIVE',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
            this.unitAreas.set(unitArea.id, unitArea);
            changed = true;
          }
        }
      }

      if (changed) {
        this.saveToStorage();
      }
    } catch (err) {
      console.warn("Legacy area migration error:", err);
    }
  }

  public async listProjects(filter?: { clientId?: string; status?: ProjectStatus }): Promise<Project[]> {
    let list = Array.from(this.projects.values());
    if (filter?.clientId && filter.clientId !== 'ALL') {
      list = list.filter(p => p.clientId === filter.clientId);
    }
    if (filter?.status) {
      list = list.filter(p => p.status === filter.status);
    }
    return list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  public async getProject(id: string): Promise<Project | null> {
    return this.projects.get(id) || null;
  }

  public async saveProject(project: Project): Promise<Project> {
    this.projects.set(project.id, { ...project });
    this.saveToStorage();
    return project;
  }

  public async deleteProject(id: string): Promise<void> {
    this.projects.delete(id);
    // Also cascade remove unit areas for this project or mark inactive
    for (const [uId, u] of this.unitAreas.entries()) {
      if (u.projectId === id) {
        this.unitAreas.delete(uId);
      }
    }
    this.saveToStorage();
  }

  public async listUnitAreas(filter?: { projectId?: string; clientId?: string; status?: UnitAreaStatus }): Promise<ProjectUnitArea[]> {
    let list = Array.from(this.unitAreas.values());
    if (filter?.projectId && filter.projectId !== 'ALL') {
      list = list.filter(u => u.projectId === filter.projectId);
    }
    if (filter?.clientId && filter.clientId !== 'ALL') {
      list = list.filter(u => u.clientId === filter.clientId);
    }
    if (filter?.status) {
      list = list.filter(u => u.status === filter.status);
    }
    return list.sort((a, b) => a.code.localeCompare(b.code));
  }

  public async listPendingUnitAreas(clientId?: string): Promise<ProjectUnitArea[]> {
    let list = Array.from(this.unitAreas.values()).filter(u => u.isPendingProjectBinding || u.projectId === 'PENDING_PROJECT');
    if (clientId && clientId !== 'ALL') {
      list = list.filter(u => u.clientId === clientId);
    }
    return list;
  }

  public async bindUnitAreaToProject(unitAreaId: string, projectId: string, clientId: string): Promise<ProjectUnitArea | null> {
    const unit = this.unitAreas.get(unitAreaId);
    if (!unit) return null;
    unit.projectId = projectId;
    unit.clientId = clientId;
    unit.isPendingProjectBinding = false;
    unit.updatedAt = new Date().toISOString();
    this.unitAreas.set(unit.id, unit);
    this.saveToStorage();
    return unit;
  }

  public async getUnitArea(id: string): Promise<ProjectUnitArea | null> {
    return this.unitAreas.get(id) || null;
  }

  public async saveUnitArea(unitArea: ProjectUnitArea): Promise<ProjectUnitArea> {
    this.unitAreas.set(unitArea.id, { ...unitArea });
    this.saveToStorage();
    return unitArea;
  }

  public async deleteUnitArea(id: string): Promise<void> {
    this.unitAreas.delete(id);
    this.saveToStorage();
  }
}
